import {
  DeploymentUnitStatus,
  ReleaseAndDeployments,
} from '@agilelab/plugin-wb-builder-common';
import { useEnvironmentsContext } from '@agilelab/plugin-wb-platform';
import { Environment } from '@agilelab/plugin-wb-platform-common';
import { useRouteRef } from '@mainstage/core-plugin-api';
import { IconButton, Menu, MenuItem, Typography } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { BaseSyntheticEvent, FC, MouseEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { createSearchParams } from 'react-router-dom';
import { releaseDetailRouteRef } from '../../../../routes';
import { useReleasePageContext } from '../../context/useReleasePageContext';
import { DeployUndeployDialogComponent } from './DeployUndeployDialog';
import { Action } from './types';

interface RowMenuProps {
  release: ReleaseAndDeployments;
}

export const RowMenu: FC<RowMenuProps> = ({ release }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const releaseDetailRoute = useRouteRef(releaseDetailRouteRef);
  const navigate = useNavigate();
  const { environmentList } = useEnvironmentsContext();
  const { deployAll, undeployAll, permissions } = useReleasePageContext();
  const [action, setAction] = useState<Action | null>(null);
  const [openDialog, setOpenDialog] = useState<{
    state: boolean;
    action?: Action;
    version?: string;
  }>({ state: false });

  const allowedEnvsList = useMemo(
    () =>
      environmentList.filter(env => {
        const pEnvName = `builder.dp.deploy.${env.name}`;

        return permissions?.data?.find(
          p => pEnvName === p.permission && p.result === 'ALLOW',
        );
      }),
    [environmentList, permissions?.data],
  );

  const undeployEnvsList = (() => {
    const deployed = new Set(
      release.deployments
        .filter(d => d.status === DeploymentUnitStatus.DEPLOYED)
        .map(e => e.environment),
    );
    return allowedEnvsList.filter(env => {
      // environments that aren't currently deployed shouldn't appear in the selection
      return deployed.has(env.name);
    });
  })();

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: BaseSyntheticEvent, activeAction: Action) => {
    event.preventDefault();
    event.stopPropagation();
    setAction(activeAction);
    setOpenDialog({
      state: true,
      action: activeAction,
      version: release.metadata.version,
    });
    closeMenu();
  };

  const handleClose = async (
    env?: Environment,
    provisionAndNavigate: boolean = false,
  ) => {
    setOpenDialog({ state: false });
    if (!provisionAndNavigate) return;
    if (env) {
      if (action === Action.Deploy) await deployAll(release, env);
      if (action === Action.Undeploy) await undeployAll(release, env);
    }

    const namespace = release.metadata.namespace ?? 'default';
    const name = release.metadata.name;
    const kind = 'Release';
    const params = env
      ? {
          env: env.name,
        }
      : undefined;

    navigate({
      pathname: releaseDetailRoute({ kind, namespace, name }),
      search: createSearchParams(params).toString(),
    });
  };

  return (
    <>
      {permissions?.anyAllowed && (
        <IconButton
          onClick={openMenu}
          size="small"
          color="secondary"
          aria-label="more"
        >
          <MoreVertIcon />
        </IconButton>
      )}
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={(e: MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
          closeMenu();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MenuItem onClick={e => handleClick(e, Action.Deploy)}>
          <Typography>Deploy</Typography>
        </MenuItem>
        <MenuItem
          disabled={!undeployEnvsList.length}
          onClick={e => handleClick(e, Action.Undeploy)}
        >
          <Typography>Undeploy</Typography>
        </MenuItem>
      </Menu>
      {openDialog.state && (
        <DeployUndeployDialogComponent
          envList={
            openDialog.action === Action.Deploy
              ? allowedEnvsList
              : undeployEnvsList
          }
          open={openDialog}
          onClose={handleClose}
          releaseEntityName={release.metadata.name}
        />
      )}
    </>
  );
};
